import React, { useEffect, useState } from "react";
import "../../../styles/logistics.css";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Layout from "../../Layout";
import Modal from "../../Modal";
import { ContainerUploadField } from "../../FormFields/RenderContainerUploadFileField";
import {
  getContainerUploads,
  deleteContainerUpload,
} from "../../../data/containerupload";
import { removeContainer, getContainers } from "../../../data/containers";
import {
  ArrowRightAlt as ArrowRightAltIcon,
  Delete as DeleteIcon,
  AddLink as AddLinkIcon,
} from "@mui/icons-material";
import { formatDateToString } from "../../Helpers";
import { showModal } from "../../../data/modal";

import {
  Grid,
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListSubheader,
  ListItem,
  ListItemText,
} from "@mui/material";
import { selectedFilteredVoyage } from "../../../selectors/filterSelector";

const ContainerUpload = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { control, register } = useForm();
  const containerUploads = useSelector((s: any) => s.containerUploads);
  const containersState = useSelector((s: any) => s.containers);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<{
    upload: any;
    containerIds: string[];
  }>({} as any);
  const [isDeleting, setIsDeleting] = useState(false);
  const { voyage } = useSelector(selectedFilteredVoyage);

  useEffect(() => {
    dispatch(getContainerUploads());
  }, [dispatch]);

  useEffect(() => {
    if (toDelete.containerIds?.length) {
      dispatch(getContainers(toDelete.containerIds));
    }
  }, [dispatch, toDelete.containerIds]);

  const handleDeleteClick = (upload: any) => {
    const containerIds = Array.isArray(upload.containers)
      ? upload.containers
      : [];

    if (containerIds.length === 0) return;

    setToDelete({ upload, containerIds });
    setConfirmOpen(true);
  };

  const handleTerminalCall = (upload: any) => {
    const containerIds = Array.isArray(upload.containers)
      ? upload.containers
      : [];

    if (containerIds.length === 0) return;

    dispatch(
      showModal({
        title: t("containerTerminalCall"),
        description: t("linkContainersToTerminalCalls"),
        form: "containerterminalcallform",
        formProps: {
          containerIds: containerIds,
        },
        size: "wide",
      })
    );
  };

  const handleConfirm = async () => {
    const { upload, containerIds } = toDelete;
    setIsDeleting(true);

    await Promise.allSettled(
      containerIds.map((uri) =>
        dispatch(removeContainer({ "@id": `/api/containers/${uri}` }))
      )
    );

    try {
      await dispatch(deleteContainerUpload(upload));
      dispatch(getContainerUploads());
      setConfirmOpen(false);
    } catch (err) {
      console.error("Error deleting upload:", err);
      alert(t("failedToDeleteUpload"));
    } finally {
      setIsDeleting(false);
    }
  };

  const groupedByLoading: Record<string, any[]> = {};
  containersState.data.forEach((c: any) => {
    const key = c.loadingTerminal?.name || t("unknown");
    if (!groupedByLoading[key]) groupedByLoading[key] = [];
    groupedByLoading[key].push(c);
  });

  return (
    <Layout>
      <Modal />
      <Grid container gap={2} flexDirection="column" padding={2}>
        <Grid item xs={12} sm={12} sx={{ height: "15vh" }}>
          <ContainerUploadField
            name="containerUploadFile"
            control={control}
            register={register}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          component={Paper}
          sx={{
            mb: 2,
            height: "65vh",
            overflow: "auto",
            position: "relative",
            "&::-webkit-scrollbar": { width: 0 },
            scrollbarWidth: "none",
            "-ms-overflow-style": "none",
            p: 1,
          }}
        >
          <Typography variant="h6" sx={{ p: 2 }}>
            {t("containerImports")}
          </Typography>

          {containerUploads.loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("fileName")}</TableCell>
                <TableCell>{t("type")}</TableCell>
                <TableCell align="center">
                  {t("numberOfContainers")}
                </TableCell>
                <TableCell align="center">{t("uploadedAt")}</TableCell>
                {/* <TableCell align="center">{t("planning")}</TableCell> */}
                <TableCell align="center">{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {containerUploads.data.map((upload: any) => (
                <TableRow key={upload.id} hover>
                  <TableCell>{upload.fileName}</TableCell>
                  <TableCell>{upload.type || "—"}</TableCell>
                  <TableCell align="center">
                    {Array.isArray(upload.containers)
                      ? upload.containers.length
                      : 0}
                  </TableCell>
                  <TableCell align="center">
                    {formatDateToString(upload.createdAt)}
                  </TableCell>
                  {/* <TableCell align="center">
                    <IconButton
                      onClick={() => handleTerminalCall(upload)}
                      size="small"
                    >
                      <AddLinkIcon />
                    </IconButton>
                  </TableCell> */}
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleDeleteClick(upload)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {containerUploads.error && (
            <Typography color="error" sx={{ p: 2 }}>
              {containerUploads.error}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={() => !isDeleting && setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
          }}
        >
          <Typography>
            {t("deleteImportedContainersConfirmationStart")}{" "}
            <strong>{toDelete.containerIds?.length || 0}</strong>{" "}
            {t("containers").toLowerCase()}
            <br />
            {t("deleteImportedContainersConfirmationEnd")} “
            <strong>{toDelete.upload?.fileName}</strong>”?
          </Typography>

          {containersState.loading || isDeleting ? (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box
              component="section"
              sx={{
                flex: 1,
                overflowY: "auto",
                maxHeight: 300,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "grey.500",
                  borderRadius: 2,
                },
              }}
            >
              <List dense disablePadding>
                {Object.entries(groupedByLoading).map(
                  ([loadingName, list]) => (
                    <li key={loadingName}>
                      <ListSubheader
                        sx={{
                          bgcolor: "background.paper",
                          backgroundColor: "#C1D8F2",
                          fontWeight: 700,
                        }}
                      >
                        {loadingName}
                      </ListSubheader>
                      {list.map((c: any) => (
                        <ListItem key={c["@id"]}>
                          <ListItemText
                            primary={`${t("uid")}: ${c.uid || "—"}`}
                            secondary={
                              <Grid container flexDirection="column">
                                <Typography
                                  component="span"
                                  variant="body2"
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  color="text.primary"
                                >
                                  {c.loadingTerminal?.name}
                                  <ArrowRightAltIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                  {c.unloadingTerminal?.name}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="body3"
                                  color="text.primary"
                                >
                                  {`${t("weight")}: ${
                                    c.weight || "—"
                                  } | ${t("type")}: ${
                                    c.containerType?.name || "—"
                                  }`}
                                </Typography>
                              </Grid>
                            }
                          />
                        </ListItem>
                      ))}
                    </li>
                  )
                )}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isDeleting}>
            {t("cancel")}
          </Button>
          <Button
            color="error"
            onClick={handleConfirm}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} /> : undefined}
          >
            {t("yesDeleteAll")}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ContainerUpload;